import { useEffect, useState } from 'react'
import { Report } from 'notiflix'
import api from '../../../helpers/apiRequester'
import { Button, KeyValueList, Modal, Spinner, StatusPill } from '../../../ui'
import { DOCUMENT_LABEL, DocumentKind, SentEmailDetail, formatDateTime, humanise, openSendDocument, sendStatusTone } from './shared'
import styles from './styles.module.scss'

/** Load one sent email. Shared by the modal and the inline pane. */
const useSentEmail = (sendId: string | null) => {
    const [detail, setDetail] = useState<SentEmailDetail | null>(null)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (!sendId) {
            setDetail(null)
            return
        }
        let cancelled = false
        setLoading(true)
        api({ url: 'comms/inbox/send', data: { sendId } })
            .then((resp) => {
                if (!cancelled && resp.data?.success) setDetail(resp.data.send)
            })
            .catch((err: any) => {
                if (!cancelled) Report.failure('Sent email', err?.response?.data?.message || 'Could not load the sent email.', 'Okay')
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [sendId])

    return { detail, loading }
}

/**
 * The email we actually sent for a LOC: headers, body, rendered PDFs and the
 * SES delivery trail (delivered / opened / bounced). Mount with a key of the
 * send id so the HTML / plain-text choice resets between emails.
 */
const SentEmailBody = ({ detail }: { detail: SentEmailDetail }) => {
    const [showHtml, setShowHtml] = useState(false)
    const [opening, setOpening] = useState<DocumentKind | null>(null)

    const openDoc = async (kind: DocumentKind) => {
        setOpening(kind)
        await openSendDocument(detail.id, kind)
        setOpening(null)
    }

    return (
        <div className={styles.stack}>
            <div className={styles.actionRow}>
                <StatusPill tone={sendStatusTone(detail.status)} dot size="sm">
                    {humanise(detail.status)}
                </StatusPill>
                {detail.documents.map((kind) => (
                    <Button key={kind} size="sm" onClick={() => openDoc(kind)} loading={opening === kind} disabled={opening !== null}>
                        {DOCUMENT_LABEL[kind]} (PDF)
                    </Button>
                ))}
                {detail.documents.length === 0 && <span className={styles.mutedInline}>No rendered documents on this send.</span>}
            </div>

            <KeyValueList
                columns
                items={[
                    { label: 'To', value: detail.toEmail, mono: true },
                    { label: 'From', value: detail.fromEmail, mono: true },
                    { label: 'Reply-To', value: detail.replyToEmail, mono: true },
                    { label: 'Sent at', value: formatDateTime(detail.sentAt) },
                    { label: 'Delivered at', value: formatDateTime(detail.deliveredAt) },
                    { label: 'Opened', value: detail.openCount ? `${detail.openCount}× · first ${formatDateTime(detail.firstOpenedAt)}` : 'not opened' },
                    { label: 'Footer ref', value: detail.footerRef, mono: true },
                    { label: 'Message-ID', value: detail.rfcMessageId, mono: true },
                    { label: 'Introducer', value: detail.introducer },
                    { label: 'Last error', value: detail.lastError },
                ]}
            />

            <div className={styles.listHeader}>
                <h3 className={styles.listTitle}>Email body</h3>
                {detail.bodyHtml && (
                    <Button size="sm" variant="link" onClick={() => setShowHtml((p) => !p)}>
                        {showHtml ? 'Show plain text' : 'Show HTML'}
                    </Button>
                )}
            </div>
            {showHtml && detail.bodyHtml ? <iframe className={styles.bodyHtml} title="Sent email HTML body" sandbox="" srcDoc={detail.bodyHtml} /> : <pre className={styles.bodyText}>{detail.bodyText || '(no plain-text body stored)'}</pre>}

            {detail.events.length > 0 && (
                <>
                    <h3 className={styles.listTitle}>Delivery trail</h3>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Event</th>
                                <th>When</th>
                                <th>Detail</th>
                            </tr>
                        </thead>
                        <tbody>
                            {detail.events.map((e) => (
                                <tr key={e.id}>
                                    <td>{humanise(e.type)}</td>
                                    <td>{formatDateTime(e.at)}</td>
                                    <td className={styles.mutedInline}>{[e.bounceType && `${e.bounceType}${e.bounceSubtype ? ` / ${e.bounceSubtype}` : ''}`, e.diagnosticCode, e.linkUrl, e.userAgent].filter(Boolean).join(' · ') || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}
        </div>
    )
}

export const SentEmailModal = ({ sendId, onClose }: { sendId: string | null; onClose: () => void }) => {
    const { detail, loading } = useSentEmail(sendId)

    return (
        <Modal open={Boolean(sendId)} onClose={onClose} size="lg" title={detail?.subject || 'Sent email'} subtitle={detail ? `${detail.claimRef} · ${detail.lenderName || '—'}` : undefined} busy={loading}>
            {!detail ? loading ? <Spinner block text="Loading…" /> : null : <SentEmailBody key={detail.id} detail={detail} />}
        </Modal>
    )
}

/** The same sent email rendered in place, for the conversation trail. */
export const SentEmailPane = ({ sendId }: { sendId: string }) => {
    const { detail, loading } = useSentEmail(sendId)

    if (!detail) return loading ? <Spinner block text="Loading the email we sent…" /> : null
    return <SentEmailBody key={detail.id} detail={detail} />
}
